/**
 * Catalog-card metadata derived purely from a game's own registered metadata
 * (`supportedModes`, `playerCounts`) and from the platform's own session list
 * — never from a `gameId` branch (CLAUDE.md §2). Shared by the catalog page
 * and the dashboard so a game is described identically on both.
 */
import type { GameCatalogEntry, SessionSummary } from '@perplexicon/api-client';

/**
 * The order modes are described in, independent of the order a game happens
 * to declare them: how you can play alone, then how many people can play
 * together.
 */
const MODE_ORDER = ['solo', 'async_multiplayer', 'round_multiplayer'] as const;

function playerCountChip(
  mode: string,
  counts: { min: number; max: number } | undefined,
): string | null {
  if (mode === 'solo') return 'Solo';
  if (!counts) return null;
  // En dash for the range, matching the typography used elsewhere in the app.
  return counts.min === counts.max
    ? `${String(counts.max)} players`
    : `${String(counts.min)}–${String(counts.max)} players`;
}

/**
 * What a metadata chip is *about*, so a caller can put the right glyph beside
 * it without matching on the rendered string. Three kinds, matching the three
 * things the chips can say: you can play this alone, this many people can play
 * it together, and it runs against a live clock.
 *
 * This is deliberately a property of the platform's own mode vocabulary, not of
 * any game — nothing here branches on a `gameId` (CLAUDE.md §2), and a new game
 * gets its glyphs purely from the modes it declares.
 */
export type GameMetaKind = 'solo' | 'players' | 'realtime';

export interface GameMetaEntry {
  kind: GameMetaKind;
  label: string;
}

/**
 * Short, comma-free metadata entries for a catalog card — "Solo", "2 players",
 * "1–10 players", "Real-time" — each tagged with what it describes. Rendered by
 * the catalog card as a single glyphed line. `Real-time` is a property of the
 * platform's round-based mode (every player acts against the same clock), so it
 * is derived from the mode vocabulary rather than declared per game.
 */
export function gameMetaEntries(
  game: Pick<GameCatalogEntry, 'supportedModes' | 'playerCounts'>,
): GameMetaEntry[] {
  const entries: GameMetaEntry[] = [];
  for (const mode of MODE_ORDER) {
    if (!game.supportedModes.includes(mode)) continue;
    const label = playerCountChip(mode, game.playerCounts[mode]);
    if (!label || entries.some((entry) => entry.label === label)) continue;
    entries.push({ kind: mode === 'solo' ? 'solo' : 'players', label });
  }
  if (game.supportedModes.includes('round_multiplayer')) {
    entries.push({ kind: 'realtime', label: 'Real-time' });
  }
  return entries;
}

/**
 * The same metadata as plain strings, for the denser surfaces that render it
 * as text only (the dashboard's `GameCard`). Kept as the shared entry point it
 * always was so those callers are unaffected by the glyphed treatment.
 */
export function gameMetaChips(
  game: Pick<GameCatalogEntry, 'supportedModes' | 'playerCounts'>,
): string[] {
  return gameMetaEntries(game).map((entry) => entry.label);
}

/**
 * `gameId -> the viewer's in-progress session for that game`, from the
 * platform's own active-session list (`GET /api/game-sessions?status=active`).
 *
 * This is what the catalog's "Active" chip means — the *viewer* has a live
 * session of that game — as opposed to `GameCatalogEntry.status`, which is
 * the game's operational state for everyone (APP_SPEC §17, §21.1). Never
 * hardcoded, and never inferred from anything but real session data
 * (APP_SPEC §18).
 *
 * The one-active-session-per-game rule (APP_SPEC §21.1) means there is
 * normally at most one; if a game ever permits several, the most recently
 * acted-on one is the one worth resuming, so that is the one kept.
 */
export function activeSessionsByGame(sessions: SessionSummary[]): Map<string, SessionSummary> {
  const byGame = new Map<string, SessionSummary>();
  for (const session of sessions) {
    const existing = byGame.get(session.gameId);
    if (!existing || existing.lastActionAt < session.lastActionAt) {
      byGame.set(session.gameId, session);
    }
  }
  return byGame;
}
